import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { getCurrentUser } from '../lib/auth';
import toast from 'react-hot-toast';
import { FaBook, FaHeart, FaStar, FaLock, FaPlay } from 'react-icons/fa';
import Skeleton from 'react-loading-skeleton';
import ReportButton from '../components/reports/ReportButton';

interface Story {
  id: string;
  title: string;
  content: string;
  languages: string[];
  audio_url: string;
  category: string;
  live_translate: boolean;
  is_promoted: boolean;
  likes: number;
  is_favorite: boolean;
  is_paid: boolean;
  cover_image: string | null;
}

function Stories() {
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [showAccessModal, setShowAccessModal] = useState(false);
  const navigate = useNavigate();
  const user = getCurrentUser();

  useEffect(() => {
    loadStories();
    checkAccess();
  }, []);

  const checkAccess = async () => {
    if (!user) return;

    try {
      // Check if user is admin
      if (user.is_admin) {
        setHasAccess(true);
        return;
      }

      // Check for valid coupon usage
      const { data: couponUsage, error } = await supabase
        .from('stripeencouponusage')
        .select('*')
        .eq('user_id', user.id)
        .gte('valid_until', new Date().toISOString());

      // If there's an error other than "no rows", throw it
      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      // User has access if there's valid coupon usage data
      setHasAccess(couponUsage && couponUsage.length > 0);
    } catch (error) {
      console.error('Error checking access:', error);
      setHasAccess(false);
    }
  };

  const loadStories = async () => {
    try {
      const { data, error } = await supabase
        .from('stripeenstory')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setStories(data || []);
    } catch (error) {
      console.error('Error loading stories:', error);
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const handleStoryClick = (story: Story) => {
    if (!user) {
      toast.error('Please log in to access stories');
      navigate('/login');
      return;
    }

    if (story.is_paid && !hasAccess) {
      setShowAccessModal(true);
      return;
    }

    navigate(`/story/${story.id}`);
  };

  const handleBuyCoupon = () => {
    navigate('/coupons');
  };

  const toggleFavorite = async (storyId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (!user) return;

    try {
      const story = stories.find(s => s.id === storyId);
      if (!story) return;

      const { error } = await supabase
        .from('stripeenstory')
        .update({ is_favorite: !story.is_favorite })
        .eq('id', storyId);

      if (error) throw error;

      setStories(stories.map(s =>
        s.id === storyId ? { ...s, is_favorite: !s.is_favorite } : s
      ));
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorite status');
    }
  };

  // Default cover image based on category
  const getDefaultCoverImage = (category: string) => {
    switch (category.toLowerCase()) {
      case 'adventure':
        return 'https://images.unsplash.com/photo-1519681393784-d120267933ba?w=800';
      case 'travel':
        return 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800';
      case 'mystery':
        return 'https://images.unsplash.com/photo-1478760329108-5c3ed9d495a0?w=800';
      default:
        return 'https://images.unsplash.com/photo-1512820790803-83ca734da794?w=800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Reading Library</h1>
          <p className="mt-2 text-gray-600">
            Improve your language skills with interactive stories
          </p>
        </div>

      {/* Access Status */}
      {!user?.is_admin && (
        <div className="mb-8 p-4 rounded-lg bg-black">
          <div className="flex items-center">
            <div className={`rounded-full p-2 mr-3 ${hasAccess ? 'bg-yellow-500' : 'bg-yellow-500'}`}>
              {hasAccess ? (
                <FaPlay className="w-5 h-5 text-black" />
              ) : (
                <FaLock className="w-5 h-5 text-black" />
              )}
            </div>
            <div>
              <h3 className={`font-semibold ${hasAccess ? 'text-yellow-400' : 'text-yellow-400'}`}>
                {hasAccess ? 'You have access to all stories' : 'Access Required'}
              </h3>
              <p className={`text-sm ${hasAccess ? 'text-white' : 'text-white'}`}>
                {hasAccess
                  ? 'Enjoy learning languages through stories!'
                  : 'Purchase a coupon to access all stories and learning materials.'}
              </p>
            </div>
            {!hasAccess && (
              <button
                onClick={handleBuyCoupon}
                className="ml-auto bg-yellow-500 text-black px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors duration-200 font-semibold"
              >
                Buy Access
              </button>
            )}
          </div>
        </div>
      )}

        {/* Stories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {loading ? (
            Array(6).fill(0).map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <Skeleton height={200} />
                <div className="p-4">
                  <Skeleton count={3} />
                </div>
              </div>
            ))
          ) : (
            stories.map((story) => (
              <div
                key={story.id}
                onClick={() => handleStoryClick(story)}
                className="bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow duration-200"
              >
                {/* Story Cover Image */}
                <div className="h-48 w-full overflow-hidden">
                  <img 
                    src={story.cover_image || getDefaultCoverImage(story.category)}
                    alt={story.title}
                  />
                </div>
                
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">{story.title}</h3>
                    <button
                      onClick={(e) => toggleFavorite(story.id, e)}
                      className={`p-2 rounded-full ${
                        story.is_favorite ? 'text-red-500' : 'text-gray-400'
                      } hover:bg-gray-100`}
                    >
                      <FaHeart />
                    </button>
                  </div>

                  <div className="flex items-center space-x-2 mb-4">
                    {story.languages.map((lang) => (
                      <span
                        key={lang}
                        className="px-2 py-1 bg-indigo-100 text-indigo-800 text-sm rounded-full"
                      >
                        {lang}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <FaBook className="text-gray-400" />
                      <span className="text-sm text-gray-600">{story.category}</span>
                    </div>
                    {story.is_promoted && (
                      <div className="flex items-center text-yellow-500">
                        <FaStar className="mr-1" />
                        <span className="text-sm">Featured</span>
                      </div>
                    )}
                  </div>

                  {story.is_paid && !hasAccess && (
                    <div className="mt-4 flex items-center text-gray-500">
                      <FaLock className="mr-2" />
                      <span className="text-sm">Premium Content</span>
                    </div>
                  )}

                 {/* Report Button */}
                 <div className="mt-3 flex justify-end">
                   <ReportButton
                     contentId={story.id}
                     contentPreview={`Story: ${story.title} - ${story.content.substring(0, 100)}...`}
                     variant="B"
                     className="text-gray-400 hover:text-red-500"
                   />
                 </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Access Required Modal */}
      {showAccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Access Required</h3>
            </div>
            <div className="p-6">
              <div className="mb-6">
                <p className="text-gray-600">
                  To access all stories and learning materials, you need to purchase a coupon worth $12.
                  Each coupon provides 30 days of unlimited access to all content on the website.
                </p>
              </div>
              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowAccessModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBuyCoupon}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Buy Coupon
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Stories;